import React from 'react';
import {useLocation} from 'wouter';

import {DeletePermanentlyStatusNotification} from '../../../../providers/notifications-center/notifications/delete';
import {Sidebar} from '../../../../components/layout/sidebar';
import {TrashBinMenu} from '../../../../components/trash-bin-menu';
import {isDeletedDocument} from '../../../../utils/document';
import {extractInfiniteQueryItems} from '../../../../components/pagination/infinite-query-items';
import {useDeleteFromTrashBinMutation} from '../hooks/use-delete-from-trash-bin-mutation';
import {useDeletedDocumentsQuery} from '../hooks/use-deleted-documents-query';
import {useDocumentQuery} from '../hooks/use-document-query';

import {PortionedDocsGroupContent} from './portioned-docs-group-content';
import {EditViewSidebarProps} from './interface';
import {SidebarHeader} from '../../../../components/layout/sidebar/sidebar-header/sidebar-header';
import {showNotification} from '../../../../providers/notifications-center/show-notification';

interface Props extends EditViewSidebarProps {
    documentId?: string;
}

export function DeletedDocuments(props: Props) {
    const deletedQuery = useDeletedDocumentsQuery();
    const deletedDocuments = extractInfiniteQueryItems(deletedQuery, (response) => response.data);
    const deleteFromTrashBin = useDeleteFromTrashBinMutation();
    const documentQuery = useDocumentQuery(props.documentId);

    const selectedDocument = documentQuery.data || null;

    const [, navigate] = useLocation();

    const handleEmptyTrashBin = async () => {
        try {
            await deleteFromTrashBin.mutateAsync();

            if (selectedDocument && isDeletedDocument(selectedDocument)) {
                navigate('/');
            }

            showNotification(<DeletePermanentlyStatusNotification />);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            showNotification(
                <DeletePermanentlyStatusNotification isError errorMessage={error.message} />
            );
        }
    };

    const header = (
        <SidebarHeader
            title="Recycle bin"
            additionalControl={
                <TrashBinMenu
                    isLoading={deleteFromTrashBin.isPending}
                    onEmptyTrashBin={handleEmptyTrashBin}
                    disabled={deletedQuery.isLoading || !deletedDocuments?.length}
                />
            }
            onClose={props.closeSidebar}
        />
    );

    return (
        <Sidebar header={header} border="right">
            <PortionedDocsGroupContent
                isInitialLoading={deletedQuery.isLoading}
                docs={deletedDocuments}
                isLoadingMore={deletedQuery.isFetchingNextPage}
                onClickLoadMoreButton={deletedQuery.fetchNextPage}
                hasNextPage={deletedQuery.hasNextPage}
                disabled={deleteFromTrashBin.isPending}
                statusIconSettings={{showReadOnly: true, showDeleted: false}}
            />
        </Sidebar>
    );
}
